import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, BookOpen } from 'lucide-react';
import {
  modulesDossier,
  FONCTIONNALITES_SIRH,
  type EtatDesModules,
  type FonctionnaliteSirh,
} from '@/services/modulesDossier.service';

interface Props {
  folderUuid?: string;
  /** Rechargé après l'ouverture du premier accès, pour que la liste suive. */
  surPremierAcces?: () => void;
}

/**
 * « Ce que ce client reçoit », et la première clé qu'on lui remet.
 *
 * OÙ CE PANNEAU VIT, ET POURQUOI PAS AILLEURS. Sur l'onglet « Accès » de la
 * fiche de dossier, qui existait déjà et qui était VIDE pour un cabinet — pire
 * qu'inutile. Aucune entrée de menu n'est ajoutée : la règle de Rose du
 * 16/08/2026 veut qu'une tâche portant sur les objets d'une liste existante se
 * branche sur cette liste.
 *
 * QUI LE VOIT. Le cabinet, et lui seul. Le serveur n'ouvre `DOSSIER × MODIFIER`
 * qu'au membre du cabinet et le refuse à la direction de l'entreprise : c'est le
 * cabinet qui paie, c'est lui qui décide. Sur un 403, le panneau se retire au
 * lieu d'afficher des interrupteurs morts.
 *
 * CE QUE LA FORME DIT. Deux blocs, et rien d'autre. Un cabinet qui ne facture
 * pas au détail bascule l'interrupteur et n'ouvre jamais les cases ; celui qui
 * facture à la fonctionnalité coche ce qu'il vend. La BDESE, elle, n'a pas de
 * cases — son contenu est fixé par la loi, et un document incomplet coûte
 * 7 500 € à l'entreprise.
 */
export function ModulesEtPremierAccesPanel({
  folderUuid,
  surPremierAcces,
}: Props) {
  const [etat, setEtat] = useState<EtatDesModules | null>(null);
  const [autorise, setAutorise] = useState(true);
  const [enCours, setEnCours] = useState(false);
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState<{
    ton: 'succes' | 'erreur';
    texte: string;
  } | null>(null);

  const recharger = useCallback(async () => {
    if (!folderUuid) return;
    try {
      setEtat(await modulesDossier.lire(folderUuid));
      setAutorise(true);
    } catch (erreur: unknown) {
      // 401/403 : ce n'est pas une panne, c'est la règle. On se retire.
      const statut = (erreur as { response?: { status?: number } })?.response
        ?.status;
      setAutorise(statut !== 401 && statut !== 403);
      setEtat(null);
    }
  }, [folderUuid]);

  useEffect(() => {
    void recharger();
  }, [recharger]);

  if (!folderUuid || !autorise || !etat) return null;

  const messageDErreur = (erreur: unknown, defaut: string) => {
    const reponse = (
      erreur as { response?: { data?: { message?: string; detail?: string } } }
    )?.response?.data;
    // Le serveur explique POURQUOI il refuse — sa phrase vaut mieux que la
    // nôtre : elle dit la règle, pas seulement l'échec.
    return reponse?.message ?? reponse?.detail ?? defaut;
  };

  const basculerSirh = async (actif: boolean) => {
    setEnCours(true);
    setMessage(null);
    try {
      // Ouvrir le SIRH sans rien dedans n'a pas de sens : on part de tout
      // ouvert, le cabinet retire ce qu'il ne vend pas.
      const fonctionnalites = actif
        ? etat.sirh.fonctionnalites.length > 0
          ? etat.sirh.fonctionnalites
          : FONCTIONNALITES_SIRH.map(f => f.valeur as FonctionnaliteSirh)
        : [];
      setEtat(
        await modulesDossier.reglerSirh(folderUuid, actif, fonctionnalites)
      );
    } catch (erreur) {
      setMessage({
        ton: 'erreur',
        texte: messageDErreur(erreur, "Ce réglage n'a pas pu être enregistré."),
      });
    } finally {
      setEnCours(false);
    }
  };

  const basculerFonctionnalite = async (cle: FonctionnaliteSirh) => {
    const actuelles = etat.sirh.fonctionnalites;
    const suivantes = actuelles.includes(cle)
      ? actuelles.filter(f => f !== cle)
      : [...actuelles, cle];
    setEnCours(true);
    setMessage(null);
    try {
      // Tout décocher ferme le module : un espace ouvert sur rien est un écran
      // vide chez le client. Le serveur le refuse aussi, on ne l'y envoie pas.
      setEtat(
        await modulesDossier.reglerSirh(
          folderUuid,
          suivantes.length > 0,
          suivantes
        )
      );
    } catch (erreur) {
      setMessage({
        ton: 'erreur',
        texte: messageDErreur(erreur, "Ce réglage n'a pas pu être enregistré."),
      });
    } finally {
      setEnCours(false);
    }
  };

  const basculerBdese = async (actif: boolean) => {
    setEnCours(true);
    setMessage(null);
    try {
      setEtat(await modulesDossier.reglerBdese(folderUuid, actif));
    } catch (erreur) {
      setMessage({
        ton: 'erreur',
        texte: messageDErreur(erreur, "Ce réglage n'a pas pu être enregistré."),
      });
    } finally {
      setEnCours(false);
    }
  };

  const ouvrirLePremierAcces = async () => {
    setEnCours(true);
    setMessage(null);
    try {
      const { email: envoyeA } = await modulesDossier.ouvrirLePremierAcces(
        folderUuid,
        email.trim()
      );
      setEmail('');
      setMessage({
        ton: 'succes',
        texte: `Le lien est parti à ${envoyeA}. C'est maintenant à la direction d'inviter les personnes de son choix.`,
      });
      surPremierAcces?.();
    } catch (erreur) {
      setMessage({
        ton: 'erreur',
        texte: messageDErreur(erreur, "L'accès n'a pas pu être ouvert."),
      });
    } finally {
      setEnCours(false);
    }
  };

  const rienDOuvert = !etat.sirh.actif && !etat.bdese.actif;

  return (
    <div className="shadow-soft mb-6 rounded-xl border border-neutral-200 bg-white p-6">
      <h2 className="text-lg font-semibold text-neutral-900">
        Ce que ce client reçoit
      </h2>
      <p className="mt-1 mb-4 text-sm text-neutral-500">
        Vous décidez de ce qui est ouvert à cette entreprise.
      </p>

      <Ligne
        titre="SIRH"
        detail={
          etat.sirh.actif
            ? `${etat.sirh.fonctionnalites.length} fonctionnalité${etat.sirh.fonctionnalites.length > 1 ? 's' : ''} sur ${FONCTIONNALITES_SIRH.length}`
            : 'Congés, absences, entretiens'
        }
        actif={etat.sirh.actif}
        enCours={enCours}
        surBascule={basculerSirh}
      />

      {etat.sirh.actif && (
        <div className="mt-2 mb-2 pl-1">
          {FONCTIONNALITES_SIRH.map(f => (
            <label
              key={f.valeur}
              className="flex cursor-pointer items-center gap-3 py-1.5 text-sm text-neutral-700"
            >
              <input
                type="checkbox"
                className="h-4 w-4 rounded border-neutral-300"
                checked={etat.sirh.fonctionnalites.includes(
                  f.valeur as FonctionnaliteSirh
                )}
                disabled={enCours}
                onChange={() =>
                  void basculerFonctionnalite(f.valeur as FonctionnaliteSirh)
                }
              />
              {f.libelle}
            </label>
          ))}
          <p className="mt-2 text-xs text-neutral-400">
            Le dossier salarié et les équipes sont toujours inclus : tout le
            reste s&apos;appuie dessus.
          </p>
        </div>
      )}

      <div className="mt-2 border-t border-neutral-100 pt-2">
        <Ligne
          titre="BDESE"
          detail="Base économique et sociale, pour les élus"
          actif={etat.bdese.actif}
          enCours={enCours}
          surBascule={basculerBdese}
        />
        {etat.bdese.actif && (
          <>
            <p className="mb-2 pl-1 text-xs text-neutral-400">
              Rien à choisir : son contenu est fixé par la loi et par
              l&apos;effectif de l&apos;entreprise.
            </p>
            {/* Le lien part d'ICI, et pas d'un onglet de plus : c'est au moment
                où l'on vient d'ouvrir la base qu'on veut y aller. */}
            <Link
              to={`/bdese/${folderUuid}`}
              className="mb-2 inline-flex items-center gap-2 pl-1 text-sm font-medium hover:opacity-80"
              style={{ color: 'var(--marque-accent)' }}
            >
              <BookOpen className="h-4 w-4" />
              Ouvrir la base de cette entreprise
            </Link>
          </>
        )}
      </div>

      {message && (
        <p
          className={`mt-4 rounded-lg px-4 py-2 text-sm ${
            message.ton === 'succes'
              ? 'bg-green-50 text-green-800'
              : 'bg-red-50 text-red-800'
          }`}
          role="status"
        >
          {message.texte}
        </p>
      )}

      {!rienDOuvert && (
        <div className="mt-5 border-t border-neutral-100 pt-5">
          <h3 className="text-sm font-semibold text-neutral-900">
            Qui y accède
          </h3>
          <p className="mt-1 mb-3 text-sm text-neutral-500">
            Ouvrez l&apos;accès à la direction de l&apos;entreprise : elle
            invitera ensuite les personnes de son choix.
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="adresse de la direction"
              className="min-w-0 flex-1 rounded-xl border px-3 py-2 text-sm shadow-sm placeholder:text-neutral-400/60 focus:ring-2 focus:outline-none"
              style={{
                borderColor: 'var(--marque-trait)',
                outlineColor: 'var(--marque-accent)',
              }}
            />
            <button
              type="button"
              disabled={enCours || email.trim().length === 0}
              onClick={() => void ouvrirLePremierAcces()}
              className="inline-flex shrink-0 items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-opacity hover:opacity-90 disabled:opacity-40"
              style={{
                backgroundColor: 'var(--marque-accent)',
                color: 'var(--marque-sur-accent)',
              }}
            >
              <UserPlus className="h-4 w-4" />
              Ouvrir l&apos;accès
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Une ligne de module : son nom, ce qu'elle contient, son interrupteur. */
function Ligne({
  titre,
  detail,
  actif,
  enCours,
  surBascule,
}: {
  titre: string;
  detail: string;
  actif: boolean;
  enCours: boolean;
  surBascule: (actif: boolean) => Promise<void>;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div>
        <p className="text-sm font-medium text-neutral-900">{titre}</p>
        <p className="text-xs text-neutral-500">{detail}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={actif}
        aria-label={`${titre} : ${actif ? 'activé' : 'non activé'}`}
        disabled={enCours}
        onClick={() => void surBascule(!actif)}
        className="relative h-6 w-11 shrink-0 rounded-full transition-colors disabled:opacity-50"
        style={{
          backgroundColor: actif ? 'var(--marque-accent)' : '#cbd5e1',
        }}
      >
        <span
          className="absolute top-1 h-4 w-4 rounded-full bg-white transition-all"
          style={{ left: actif ? '1.5rem' : '0.25rem' }}
        />
      </button>
    </div>
  );
}
